import { capitalize } from "lodash"

const isClientSide = typeof window !== "undefined"

const isProduction = (): boolean => {
  if (!isClientSide) {
    console.error(
      "isProduction() called on the server! This function is meant for client-side only. Use isProductionServer() instead.",
    )
    return false
  }

  const host = window.location.hostname.toLowerCase()
  return host.startsWith("app.hakio.com")
}

const isStaging = (): boolean => {
  if (!isClientSide) {
    console.warn("isStaging() called on the server! This function is meant for client-side only.")
    return false
  }

  const host = window.location.hostname.toLowerCase()
  if (host.startsWith("localhost")) return import.meta.env.VITE_RUNTIME_ENVIRONMENT === "staging"
  return host.startsWith("staging.app.hakio.com")
}

const isDemo = (): boolean => {
  if (!isClientSide) {
    console.warn("isDemo() called on the server! This function is meant for client-side only.")
    return false
  }

  const host = window.location.hostname.toLowerCase()
  if (host.startsWith("localhost")) return import.meta.env.VITE_RUNTIME_ENVIRONMENT === "demo"
  return host.startsWith("demo.app.hakio.com") || host.startsWith("hakio-core-demo-")
}

const isDevelopment = (): boolean => {
  if (!isClientSide) {
    console.warn(
      "isDevelopment() called on the server! This function is meant for client-side only.",
    )
    return false
  }

  const host = window.location.hostname.toLowerCase()
  if (host.startsWith("localhost")) {
    const runtimeEnv = import.meta.env.VITE_RUNTIME_ENVIRONMENT
    return !runtimeEnv || runtimeEnv === "local" || runtimeEnv === "development"
  }
  return host.startsWith("dev.app.hakio.com") || host.startsWith("hakio-core-")
}

const isLocal = (): boolean => {
  if (!isClientSide) {
    console.warn("isLocal() called on the server! This function is meant for client-side only.")
    return false
  }
  return window.location.hostname.toLowerCase().startsWith("localhost")
}

function extractSubdomains(): string[] | null {
  if (typeof window === "undefined") {
    return null
  }
  const origin = window.location.origin
  const hostname = new URL(origin).hostname
  const baseDomain = "app.hakio.com"

  if (hostname === baseDomain) {
    return null
  }

  if (hostname.includes("localhost")) {
    return ["local"]
  }

  const parts = hostname.split(".")
  const baseParts = baseDomain.split(".")

  if (parts.length > baseParts.length) {
    return parts.slice(0, parts.length - baseParts.length)
  }

  return null
}

const appTitleName = () => {
  const subdomains = extractSubdomains()
  return "Hakio" + (subdomains?.map((subdomain) => ` - ${capitalize(subdomain)}`).join("") ?? "")
}

/** Public client-side IDs of the LaunchDarkly environments. The server reads LaunchDarkly through
 * them too, so both see the same flags. */
const LAUNCHDARKLY_CLIENT_SIDE_IDS = {
  prod: "6548ad6dc00e6012abe07d64",
  demo: "658068e07a319d1087ba561d",
  test: "6548ad6dc00e6012abe07d63",
} as const

const launchDarklyClientSideID = (): string => {
  if (import.meta.env.VITE_USE_PRODUCTION_FLAGS === "true") {
    // Prod env — feature branches shared with customers
  } else if (isProduction()) {
    // Escape and go to default
  } else if (isDemo()) {
    return LAUNCHDARKLY_CLIENT_SIDE_IDS.demo
  } else if (isDevelopment() || isStaging()) {
    return LAUNCHDARKLY_CLIENT_SIDE_IDS.test
  }

  return LAUNCHDARKLY_CLIENT_SIDE_IDS.prod
}

// Public client-side tokens. The Amplitude proxy handler rejects any other key so our
// origin can't relay into someone else's project.
const AMPLITUDE_API_KEYS = {
  production: "3ed520003799951ab33d526dc0edc5db",
  development: "396cef091309135f309ab16d7a9b2881",
} as const

const amplitudeProductionAPIKey = () => {
  return AMPLITUDE_API_KEYS.production
} // Production is always used when fetching data (but not when tracking)

const amplitudeAPIKey = () => {
  if (isProduction()) {
    return amplitudeProductionAPIKey()
  } else {
    return AMPLITUDE_API_KEYS.development
  }
}

// HAK-2306: Amplitude only runs when the production SDK key is in use. In dev/staging/demo/
// local the SDK is fully disabled (no init, no session replay, no tracking) so dev events
// don't pollute analytics / inflate active-user counts.
//
// VITE_ENABLE_AMPLITUDE is a local-only escape hatch for verifying new events against
// Hakio-Dev; no deployed environment sets it, so HAK-2306's guarantee holds.
const canReloadCompanies = (): boolean => {
  if (!isClientSide) return false
  return !isProduction() && !isDemo()
}

const isAmplitudeEnabled = (): boolean => {
  return (
    amplitudeAPIKey() === amplitudeProductionAPIKey() ||
    import.meta.env.VITE_ENABLE_AMPLITUDE === "true"
  )
}

const survicateWorkspaceKey = () => {
  return "33436b88bad3e5df3bf1070469362bd3"
}

const canAccessDataValidation = (lifecycleStage?: string | null): boolean => {
  return (
    !lifecycleStage ||
    lifecycleStage === "data_setup" ||
    lifecycleStage === "poc" ||
    lifecycleStage === "not_customer" ||
    isDemo()
  )
}

export {
  canReloadCompanies,
  AMPLITUDE_API_KEYS,
  amplitudeAPIKey,
  amplitudeProductionAPIKey,
  appTitleName,
  canAccessDataValidation,
  extractSubdomains,
  isAmplitudeEnabled,
  isDemo,
  isDevelopment,
  isLocal,
  isStaging,
  isProduction,
  LAUNCHDARKLY_CLIENT_SIDE_IDS,
  launchDarklyClientSideID,
  survicateWorkspaceKey,
}
